import React, { useState, useEffect } from 'react';
import { getUsers, TASK_STATUSES } from '../data/supabaseApi';
import { useToast } from '../context/ToastContext';
import './TaskForm.css';

const TaskForm = ({ onSubmit, initialData = null }) => {
  const { addToast } = useToast();
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    taskName: initialData?.taskName || '',
    clientName: initialData?.clientName || '',
    projectName: initialData?.projectName || '',
    estimatedTime: initialData?.estimatedTime ?? '',
    taskDescription: initialData?.taskDescription || '',
    assignedMember: initialData?.assignedMember || initialData?.userName || '',
    plannedStartDate: initialData?.plannedStartDate || '',
    plannedEndDate: initialData?.plannedEndDate || '',
    currentStatus: initialData?.currentStatus || 'To Do',
    delayReason: initialData?.delayReason || '',
    actualTime: initialData?.actualTime ?? '',
    completionDate: initialData?.completionDate || ''
  });

  useEffect(() => {
    let cancelled = false;
    getUsers().then((allUsers) => {
      if (cancelled) return;
      setUsers(allUsers);
      if (!initialData) {
        const savedUser = localStorage.getItem('selectedUserName');
        const defaultMember = savedUser || (allUsers.length > 0 ? allUsers[0].name : '');
        setFormData(prev => ({
          ...prev,
          assignedMember: defaultMember
        }));
      }
    });
    return () => { cancelled = true; };
  }, [initialData]);

  useEffect(() => {
    const handleUserChange = () => {
      const savedUser = localStorage.getItem('selectedUserName');
      if (savedUser && !initialData) {
        setFormData(prev => ({ ...prev, assignedMember: savedUser }));
      }
    };
    window.addEventListener('userNameChanged', handleUserChange);
    return () => window.removeEventListener('userNameChanged', handleUserChange);
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: (name === 'estimatedTime' || name === 'actualTime') ? (value === '' ? '' : parseFloat(value) || 0) : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.taskName?.trim() || !formData.clientName?.trim() || !formData.projectName?.trim()) {
      addToast('Please fill in Task Name, Client Name, and Project Name.', 'error');
      return;
    }
    if (!formData.assignedMember) {
      addToast('Please select an assigned member.', 'error');
      return;
    }
    if (formData.currentStatus === 'Delayed' && !formData.delayReason?.trim()) {
      addToast('Delay Reason is required when status is Delayed.', 'error');
      return;
    }
    const user = users.find(u => u.name === formData.assignedMember);
    const payload = {
      ...formData,
      userId: user?.id || 0,
      estimatedTime: formData.estimatedTime === '' ? 0 : Number(formData.estimatedTime),
      actualTime: formData.actualTime === '' ? undefined : Number(formData.actualTime)
    };
    if (payload.currentStatus === 'Completed' && !payload.completionDate) {
      payload.completionDate = new Date().toISOString().split('T')[0];
    }
    onSubmit(payload);
    if (!initialData) {
      setFormData({
        taskName: '',
        clientName: '',
        projectName: '',
        estimatedTime: '',
        taskDescription: '',
        assignedMember: users[0]?.name || '',
        plannedStartDate: '',
        plannedEndDate: '',
        currentStatus: 'To Do',
        delayReason: '',
        actualTime: '',
        completionDate: ''
      });
    }
  };

  const isDelayed = formData.currentStatus === 'Delayed';

  return (
    <form className="task-form" onSubmit={handleSubmit}>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="taskName">Task Name *</label>
          <input
            type="text"
            id="taskName"
            name="taskName"
            value={formData.taskName}
            onChange={handleChange}
            placeholder="e.g. Implement login API"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="clientName">Client Name *</label>
          <input
            type="text"
            id="clientName"
            name="clientName"
            value={formData.clientName}
            onChange={handleChange}
            placeholder="Client or product name"
            required
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="projectName">Project Name *</label>
          <input
            type="text"
            id="projectName"
            name="projectName"
            value={formData.projectName}
            onChange={handleChange}
            placeholder="Project or website name"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="assignedMember">Assigned Member *</label>
          <select
            id="assignedMember"
            name="assignedMember"
            value={formData.assignedMember}
            onChange={handleChange}
            required
          >
            <option value="">— Select member —</option>
            {users.map(u => (
              <option key={u.id} value={u.name}>{u.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-group full-width">
        <label htmlFor="taskDescription">Task Description</label>
        <textarea
          id="taskDescription"
          name="taskDescription"
          value={formData.taskDescription}
          onChange={handleChange}
          placeholder="Brief description of the task"
          rows={3}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="estimatedTime">Estimated Time (hours) *</label>
          <input
            type="number"
            id="estimatedTime"
            name="estimatedTime"
            value={formData.estimatedTime}
            onChange={handleChange}
            min="0"
            step="0.5"
            placeholder="0"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="plannedStartDate">Planned Start Date</label>
          <input
            type="date"
            id="plannedStartDate"
            name="plannedStartDate"
            value={formData.plannedStartDate}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="plannedEndDate">Planned End Date</label>
          <input
            type="date"
            id="plannedEndDate"
            name="plannedEndDate"
            value={formData.plannedEndDate}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="currentStatus">Current Status *</label>
          <select
            id="currentStatus"
            name="currentStatus"
            value={formData.currentStatus}
            onChange={handleChange}
            required
          >
            {TASK_STATUSES.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        {formData.currentStatus === 'Completed' && (
          <>
            <div className="form-group">
              <label htmlFor="actualTime">Actual Time (hours)</label>
              <input
                type="number"
                id="actualTime"
                name="actualTime"
                value={formData.actualTime}
                onChange={handleChange}
                min="0"
                step="0.5"
                placeholder="0"
              />
            </div>
            <div className="form-group">
              <label htmlFor="completionDate">Completion Date</label>
              <input
                type="date"
                id="completionDate"
                name="completionDate"
                value={formData.completionDate}
                onChange={handleChange}
              />
            </div>
          </>
        )}
      </div>

      {isDelayed && (
        <div className="form-group full-width delay-reason-group">
          <label htmlFor="delayReason">Delay Reason *</label>
          <textarea
            id="delayReason"
            name="delayReason"
            value={formData.delayReason}
            onChange={handleChange}
            placeholder="Reason for delay (required)"
            rows={2}
            required
          />
        </div>
      )}

      <div className="form-actions">
        <button type="submit" className="submit-btn">
          {initialData ? 'Update Task' : 'Add Task'}
        </button>
      </div>
    </form>
  );
};

export default TaskForm;
